import { type ApolloError, useMutation } from '@apollo/client';
import { Button, CircularProgress } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useContext, useEffect } from 'react';
import { CALLBACK_URL, HOME_PATH } from 'src/constants';
import AuthContext from 'src/context/AuthContext';
import { buildOauth2login } from 'src/dynamoQueries/oauth';
import { getTokenExpirationMinutes, setTokenAndExpire } from 'src/helpers/authHelper';
import { CallbackStyles } from './styles';

interface OAuth2LoginPayload {
  exp: number;
}

interface OAuth2LoginResult {
  token: string;
  refreshExpiresIn: number;
  payload: OAuth2LoginPayload;
}

interface OAuth2LoginData {
  oauth2Login: OAuth2LoginResult;
}

interface MainProps {
  children: ReactNode;
}

const Main: React.FC<MainProps> = ({ children }) => {
  const styles = CallbackStyles();

  return <div className={styles.main}>{children}</div>;
};

export const Callback: React.FC = () => {
  const styles = CallbackStyles();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [executeOauthLogin, { data, loading, error }] = useMutation<OAuth2LoginData>(
    buildOauth2login(true, {
      authResponse: window.location.toString(),
      callbackUrl: CALLBACK_URL(),
    }),
  );

  const goHome = (): void => {
    navigate({ to: HOME_PATH });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: executeOauthLogin should only run once on mount
  useEffect(() => {
    executeOauthLogin();
  }, []);

  const handleSuccessLogin = (): void => {
    if (!data?.oauth2Login) return;

    const { token, refreshExpiresIn, payload } = data.oauth2Login;
    setTokenAndExpire(token, refreshExpiresIn, payload.exp);
    const tokenExp = getTokenExpirationMinutes(payload.exp);
    authContext.setTokenExpiration?.(tokenExp);
    authContext.setHasToken?.(true);
    const push = localStorage.getItem('originalURI');
    if (push) {
      window.location.href = push;
    }
  };

  if (loading)
    return (
      <Main>
        <CircularProgress color="secondary" />
      </Main>
    );

  if (error)
    return (
      <Main>
        <div className={styles.buttonParent}>
          <div className={styles.error}>Error occurred! {(error as ApolloError)?.message}</div>
          <Button onClick={goHome} variant="contained" color="primary">
            Return Library
          </Button>
        </div>
      </Main>
    );

  if (data) {
    handleSuccessLogin();
    return null;
  }

  return null;
};
